import React from "react";
export type ProviderTabs = any;
export type MappedTabItem = {
  label: string;
  value: any;
  type?:
    | "website"
    | "needType"
    | "documentLink"
    | "phone"
    | "email"
    | "linkAccount";
  linkText?: string;
};
const ENABLE_SERVICES_TAB = false;

export const mapProviderTabsToData = (
  providerTabs: ProviderTabs,
  isPlannerSection?: boolean
): MappedTabItem[][] => {
  if (!providerTabs) return [];

  const bizInfo = providerTabs?.businessInformation;
  const bizDetails = providerTabs?.businessDetails ?? bizInfo;
  const primaryContactPhone = providerTabs?.primaryInformation?.contactPhone;
  const bizContactPhone = (bizInfo as Record<string, unknown> | undefined)
    ?.contactPhone as string | undefined;
  const plannerBusinessPhone =
    primaryContactPhone ||
    bizContactPhone ||
    bizInfo?.businessPhone ||
    "-";
  const businessInfo = [
    { label: "Business Name", value: bizInfo?.businessName || "-" },
    { label: "Business Type", value: bizInfo?.businessType || "-" },
    { label: "Years in Operation", value: bizInfo?.yearsInOperation ?? "-" },
    {
      label: "Business Address",
      value: bizInfo?.address ? `${bizInfo.address?.streetLine1 || ""}` : "-",
    },
    { label: "City", value: bizInfo?.address?.city || "-" },
    { label: "State", value: bizInfo?.address?.state || "-" },
    { label: "ZIP Code", value: bizInfo?.address?.zipcode || "-" },
    { label: "Country", value: bizInfo?.address?.country || "-" },
    {
      label: "Business Phone",
      value: isPlannerSection ? plannerBusinessPhone : bizInfo?.businessPhone || "-",
      type: "phone" as const,
    },
    { label: "Business Email", value: bizInfo?.businessEmail || "-", type: "email" as const },
    { label: "Business Website", value: bizInfo?.businessWebsite || "-", type: "website" as const },
    { label: "Business Description", value: bizDetails?.businessDescription || "-" },
    {
      label: "Need Type",
      value: Array.isArray(bizDetails?.needTypes) ? bizDetails.needTypes : [],
      type: "needType" as const,
    },
    {
      label: "Pricing Model / Packages",
      value: (() => {
        const pd = bizDetails?.pricingDocument;
        if (!pd) return "-";
        if (typeof pd === "string") return pd;
        return pd?.name ?? pd?.fileName ?? pd?.url ?? "-";
      })(),
    },
    {
      label: "Emergency / 24×7 Service Availability",
      value: bizDetails?.emergencyServiceAvailable ? "Available 24/7" : "No",
    },
    {
      label: "Business License Document",
      value: (() => {
        const doc = providerTabs?.documents?.businessLicenseDocuments?.[0];
        return doc?.url || "-";
      })(),
      type: "documentLink" as const,
    },
  ];

  const primaryInfo = [
    {
      label: "Primary Contact Name",
      value: providerTabs?.primaryInformation?.contactName || "-",
    },
    {
      label: "Contact Role / Designation",
      value: providerTabs?.primaryInformation?.contactRole || "-",
    },
    {
      label: "Primary Contact Phone",
      value: providerTabs?.primaryInformation?.contactPhone || "-",
    },
    {
      label: "Primary Contact Email",
      value: providerTabs?.primaryInformation?.contactEmail || "-",
    },
    {
      label: "Linked Accounts",
      value: providerTabs?.primaryInformation?.linked_accounts?.crmTenantName
        ? `CRM Partner Name : ${providerTabs.primaryInformation.linked_accounts.crmTenantName || "-"}`
        : "-",
      type: !providerTabs?.primaryInformation?.linked_accounts?.crmTenantName
        ? "linkAccount"
        : undefined,
    },
  ];

  const locationMedia = providerTabs?.location_media_details ?? providerTabs?.locationAndMedia;
  const locationAndMedia = (() => {
    if (locationMedia?.serviceableLocation?.length) {
      const locationRows = locationMedia.serviceableLocation.map((loc: any, i: number) => {
        const parts = [loc.city, loc.state, loc.country, loc.zipcode].filter(Boolean);
        return { label: `Serviceable Location ${i + 1}`, value: parts.length ? parts.join(", ") : "-" };
      });
      if (locationMedia?.providerMedia?.length) {
        locationMedia.providerMedia.forEach((m: any, i: number) => {
          locationRows.push({ label: `Media ${i + 1}`, value: m?.fileName || (m?.mediaType === "image" ? "Image" : "Document") });
        });
      }
      return locationRows;
    }
    if (locationMedia?.providerMedia?.length) {
      return locationMedia.providerMedia.map((m: any, i: number) => ({
        label: `Media ${i + 1}`,
        value: m?.fileName || (m?.mediaType === "image" ? "Image" : "Document"),
      }));
    }
    if (locationMedia && typeof locationMedia === "object" && !Array.isArray(locationMedia)) {
      return Object.entries(locationMedia).map(([key, val]) => ({
        label: String(key).replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
        value: val != null && val !== "" ? String(val) : "-",
      }));
    }
    return [{ label: "No location or media details", value: "-" }];
  })();

  const servicesTab = ENABLE_SERVICES_TAB
    ? (providerTabs?.services?.data?.flatMap((service: any) =>
      service?.subServices?.map((sub: any) => ({
        label: service?.serviceName ?? "-",
        value: sub?.subServiceName ?? "-",
      })),
    ) ?? [])
    : [];

  const docs = providerTabs?.documents;
  const documentation =
    docs && (docs.providerLicenceDocuments?.length || docs.providerDocuments?.length)
      ? [
        ...(docs.providerLicenceDocuments || []).map((d: any) => ({
          label: d?.licenceNumber ?? "Licence",
          value: d?.document?.[0]?.url ?? d?.document?.[0]?.fileName ?? "-",
        })),
        ...(docs.providerDocuments || []).map((d: any) => ({
          label: d?.fileName ?? "Document",
          value: d?.url ?? "-",
        })),
      ]
      : providerTabs?.documentation && Array.isArray(providerTabs.documentation)
        ? providerTabs.documentation.map((doc: any) => ({
          label: doc?.name || doc?.label || "Document",
          value: doc?.url || doc?.downloadUrl || doc?.fileName || "-",
        }))
        : [{ label: "No documentation", value: "-" }];

  const bankData = providerTabs?.banking_information ?? providerTabs?.bankDetails;
  const BANK_LABEL_MAP: Record<string, string> = {
    bankTaxProfileEIN: "Tax Profile/EIN",
    bankRoutingNumberABA: "Routing Number ABA",
    bankAccountType: "Account Type",
    bankAccountNumber: "Account Number",
    bankAccountHolderName: "Account Holder Name",
    w9Form: "W9 Form",
  };
  const BANK_FIELD_ORDER = [
    "bankTaxProfileEIN",
    "bankAccountType",
    "bankAccountHolderName",
    "bankRoutingNumberABA",
    "bankAccountNumber",
    "w9Form",
  ];
  const getBankLabel = (key: string) =>
    BANK_LABEL_MAP[key] ?? BANK_LABEL_MAP[key.charAt(0).toLowerCase() + key.slice(1)] ?? String(key).replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
  const formatBankValue = (val: unknown): string =>
    Array.isArray(val) ? (val.length ? String(val.length) + " file(s)" : "-") : (val != null && val !== "" ? String(val) : "-");
  const getW9FormItem = (val: unknown): { value: string; type?: "documentLink"; linkText?: string } => {
    if (!Array.isArray(val) || val.length === 0) return { value: formatBankValue(val) };
    const first = val[0];
    const url = typeof first === "string" ? first : (first && typeof first === "object" && "url" in first ? (first as { url?: string }).url : null);
    if (url) return { value: url, type: "documentLink", linkText: "W9 Form" };
    return { value: formatBankValue(val) };
  };
  const bankDetails =
    bankData && typeof bankData === "object" && !Array.isArray(bankData)
      ? BANK_FIELD_ORDER.filter((key) => key in bankData).map((key) => {
        const val = (bankData as Record<string, unknown>)[key];
        if (key === "w9Form") {
          const w9 = getW9FormItem(val);
          return { label: getBankLabel(key), ...w9 };
        }
        return { label: getBankLabel(key), value: formatBankValue(val) };
      })
      : [{ label: "No bank details", value: "-" }];

  if (isPlannerSection) {
    return [businessInfo, primaryInfo, locationAndMedia, [], documentation, bankDetails];
  }
  return [businessInfo, primaryInfo, locationAndMedia, servicesTab, documentation, bankDetails];
};
